import { TIER_DUEL_ESTIMATES } from './tier-duel-estimates.js';
import { displayTotalMinor, taxNoteText } from './store-display-price.js';

/**
 * The pass section: ONE period control, ONE button per tier.
 *
 * It used to be four buttons across two cards — monthly and yearly on each —
 * which made the annual saving something a player had to compute from two
 * labels while four calls to action competed. A shared Monthly/Yearly control
 * halves the buttons and makes the two prices directly comparable.
 *
 * Extracted from `store.js` so its output can be asserted as MARKUP. A source
 * grep cannot see whether the selected period actually reaches the plan the
 * button will buy, which is the one thing here that costs real money if wrong.
 */

export { TIER_RANK };

export const PASS_PERIODS = Object.freeze(['month', 'year']);

/**
 * Monthly, deliberately.
 *
 * Yearly converts better as a default in most SaaS, but the owner's stated
 * worry is the opposite one: "least scaring away people with big sums like 200
 * usd". Defaulting to yearly puts $200 on the screen the moment the store
 * opens. The yearly tab carries "2 months free", so the upsell is visible
 * without being sprung — and pre-selecting the larger charge is also the shape
 * consumer regulators read as a pre-checked upsell.
 */
export const DEFAULT_PASS_PERIOD = 'month';

/** Mirrors `game/utils/stripeBillingVerification.js`. Kept honest by test. */
export const PASS_TIER_PRICES_MINOR = Object.freeze({
  moonfall_storyteller: Object.freeze({ month: 1000, year: 10000 }),
  moonfall_manga: Object.freeze({ month: 2000, year: 20000 }),
});

const PLAN_TYPES = Object.freeze({
  moonfall_storyteller: Object.freeze({ month: 'storyteller_monthly', year: 'storyteller_yearly' }),
  moonfall_manga: Object.freeze({ month: 'manga_monthly', year: 'manga_yearly' }),
});

/* Which tier is "above" which. Mirrors the budget ladder the server reads
   from `MONTHLY_GAME_LOSS_ALLOWANCE_USD_MICROS`; a client cannot import that,
   so `storePassCards.test.js` pins the two orders together. */
const TIER_RANK = Object.freeze({ moonfall_storyteller: 1, moonfall_manga: 2 });

/**
 * Where the Moonlight "+" leads, by what the account holds (the owner,
 * 2026-09-21): no pass, the passes themselves; a pass with a tier above it,
 * that tier — the upgrade; the top pass, the Refill, the only more Moonlight
 * left to buy. Pure, so the ladder can be pinned beside the tier order.
 * @param {string | null | undefined} activeTier
 * @returns {{ kind: 'passes' } | { kind: 'upgrade', tier: string } | { kind: 'refill' }}
 */
export function moonlightPlusTarget(activeTier) {
  const rank = activeTier ? TIER_RANK[activeTier] : undefined;
  if (!rank) return { kind: 'passes' };
  const above = Object.keys(TIER_RANK).find((tier) => TIER_RANK[tier] === rank + 1);
  return above ? { kind: 'upgrade', tier: above } : { kind: 'refill' };
}

const MONTHS_PER_YEAR = 12;

export function usd(minor) {
  const whole = minor / 100;
  return `$${Number.isInteger(whole) ? whole : whole.toFixed(2)}`;
}

/**
 * A weekly refill is one quarter of the holder's monthly rate. Existing
 * monthly cycles retain their original price until they move to weekly resets.
 * The server's rounding rule is mirrored so checkout and its label agree.
 * @param {{ subscriptionType?: string, interval?: string, weeklyReset?: boolean }} [holder]
 * @returns {number | null}
 */
export function refillPriceMinor(holder = {}) {
  const { subscriptionType, interval, weeklyReset = false } = holder;
  const prices = PASS_TIER_PRICES_MINOR[subscriptionType];
  if (!prices) return null;
  const monthly = interval === 'year' ? prices.year / MONTHS_PER_YEAR : prices.month;
  return Math.floor(monthly / (weeklyReset ? 4 : 1));
}

/** What a year costs per month, so the sticker is never the only figure shown. */
function monthlyEquivalent(yearMinor) {
  return `$${(yearMinor / 100 / MONTHS_PER_YEAR).toFixed(2)}`;
}

/**
 * Every price these cards print, so a caller can check the whole screen is
 * covered before it switches to tax-inclusive figures.
 */
export function passCardAmountsMinor() {
  const amounts = [];
  for (const prices of Object.values(PASS_TIER_PRICES_MINOR)) {
    amounts.push(prices.month, prices.year);
  }
  return amounts;
}

export function buildPassPeriodSelector({ htmlText, period, yearlyEnabled = false }) {
  /* NO YEARLY, NO CONTROL. The server refuses a yearly checkout outright, so
     this is not what makes the rule true — it is what stops the store
     advertising a plan the server will reject. A visible tab that 400s reads as
     the product being broken rather than as a plan we do not sell.

     It disappears entirely rather than leaving a lone "Monthly" tab, because a
     one-option control is a decision the player does not have. Defaults to
     hidden, matching `SUBSCRIPTION_YEARLY_ENABLED`, so a caller that forgets to
     pass the flag cannot offer more than the server will honour. */
  if (!yearlyEnabled) return '';

  const option = (value, label, note) => `
    <button type="button" class="store-period__option${period === value ? ' is-selected' : ''}"
            data-store-period="${value}" aria-pressed="${period === value}">
      <span class="store-period__label">${label}</span>
      ${note ? `<span class="store-period__note">${note}</span>` : ''}
    </button>`;

  return `
    <div class="store-period" role="group" aria-label="${htmlText('store.billingPeriod', 'Billing period')}">
      ${option('month', htmlText('store.periodMonthly', 'Monthly'), '')}
      ${option('year', htmlText('store.periodYearly', 'Yearly'), htmlText('store.twoMonthsFree', '2 months free'))}
    </div>`;
}

const PASS_TITLE_KEYS = Object.freeze({
  moonfall_storyteller: ['store.storytellerPass', 'Storyteller Pass'],
  moonfall_manga: ['store.mangaPass', 'Manga Pass'],
});

function passTitle(htmlText, tier) {
  const [key, fallback] = PASS_TITLE_KEYS[tier] || PASS_TITLE_KEYS.moonfall_manga;
  return htmlText(key, fallback);
}

/**
 * The plan behind a gift, if any, as the held card says it:
 *  - DEFERRED: a paid plan whose next normal billing cycle starts when the
 *    gift ends. Remaining paid weeks are not saved (vouchersData.js).
 *  - SCHEDULED: a plan bought during the gift, held by Stripe until it ends.
 * Neither can be changed until then, so the cards route to the portal.
 */
function giftPlanLine(htmlText, giftPlan, giftFourWeeks) {
  if (giftPlan?.kind === 'deferred') {
    if (giftPlan.status !== 'applied') return giftPlan.status === 'failed'
      ? htmlText('voucher.storeGiftDeferralFailed', 'Your gift is active, but the billing change has not succeeded. Open Manage Subscription to check your next charge; its delay is not confirmed.')
      : htmlText('voucher.storeGiftDeferralPending', 'Your gift is active. The billing change is not confirmed yet. Open Manage Subscription to check your next charge.');
    const key = giftFourWeeks ? 'voucher.storeDeferred' : 'voucher.storeDeferredLegacy';
    const fallback = giftFourWeeks
      ? 'Your paid {{pass}} next normal billing cycle starts {{date}}, when the gift ends, with a fresh normal weekly bar. Your next charge is {{renews}}. Remaining paid weeks are not saved.'
      : 'Your {{pass}} becomes active again when the gift ends on {{date}}. Its next charge remains {{renews}}. Manage billing in the portal.';
    return htmlText(key, fallback, {
      pass: passTitle(htmlText, giftPlan.tier), date: giftPlan.startsAt, renews: giftPlan.renewsAt,
    });
  }
  if (giftPlan?.kind === 'scheduled') {
    return htmlText('voucher.storeScheduled', 'Your {{pass}} starts {{date}}, when your gift ends.', {
      pass: passTitle(htmlText, giftPlan.tier), date: giftPlan.startsAt,
    });
  }
  return '';
}

function buildCard({
  htmlText, period, tier, icon, badge, highlight, titleKey, titleFallback, features,
  activeTier, activeInterval, complimentary = false, complimentaryEndsAt = '', giftPlan = null,
  tax = null, giftFourWeeks = false,
}) {
  const prices = PASS_TIER_PRICES_MINOR[tier];
  const isYear = period === 'year';
  /* WHAT THIS BUYER PAYS, not what the seller nets. Where Stripe collects no
     tax these are the list prices unchanged; where it collects, they are the
     totals its checkout will charge — including the saving, which is the
     difference between two totals rather than a net figure dressed up as one. */
  const monthMinor = displayTotalMinor(prices.month, tax);
  const yearMinor = displayTotalMinor(prices.year, tax);
  const headline = isYear ? monthlyEquivalent(yearMinor) : usd(monthMinor);
  // The saving is the two months a 10x annual ratio gives back, stated as the
  // money it actually is — the percentage is identical on both tiers and so
  // carries no information the tab has not already given.
  const saving = isYear
    ? htmlText('store.billedYearly', '{{total}} billed yearly · save {{saved}}', {
      total: usd(yearMinor),
      saved: usd((monthMinor * MONTHS_PER_YEAR) - yearMinor),
    })
    : '&nbsp;';

  /* A pass holder cannot re-buy what they hold — `server.js` answers 409 to
     that — but a tier CHANGE is not a second subscription: it modifies the
     existing one in place. So the held pass routes to billing, and the tier
     ABOVE is genuinely buyable, labelled as the upgrade it is. A tier below is
     a downgrade, which is deferred to period end and belongs in the portal. */
  const isTheHeldPass = activeTier === tier;
  /* A GIFT (a redeemed voucher) marks the card of the tier the account plays.
     With no plan behind it every button is a fresh Subscribe: the new pass
     starts when the gift ends (server.js, giftCheckoutSchedule.js). A plan
     the gift deferred, or one already scheduled after it, waits in a Stripe
     trial and is not changed until the gift is over: the held card manages
     it, the other card routes to the portal. */
  const giftWaits = complimentary && Boolean(giftPlan);
  const upgradeable = !complimentary && activeTier != null && !isTheHeldPass && TIER_RANK[tier] > TIER_RANK[activeTier];
  /* A HOLDER CAN STILL PREPAY. Switching their own pass to the annual price is
     a change the server performs in place; refusing it meant cancelling and
     re-buying to reach the yearly rate. Only the reverse — shortening a
     commitment already paid for — defers to the billing portal. */
  const prepayable = !complimentary && isTheHeldPass && period === 'year' && activeInterval === 'month';
  const cardBadge = isTheHeldPass
    ? (complimentary ? htmlText('voucher.storeComplimentaryBadge', 'GIFT') : htmlText('store.active', 'ACTIVE'))
    : badge;
  const complimentaryLine = complimentary && isTheHeldPass
    ? `<p class="plan-complimentary">${htmlText('voucher.storeComplimentary', 'Gift pass · ends {{date}}', { date: complimentaryEndsAt })}</p>`
      + (giftPlan ? `<p class="plan-complimentary plan-complimentary--plan">${giftPlanLine(htmlText, giftPlan, giftFourWeeks)}</p>` : '')
      + `<p class="plan-complimentary">${giftFourWeeks
        ? htmlText('voucher.storeWeeklyPortions', 'A gift lasts 28 days with a fresh 100% base each week. Old base Moonlight and unused leftovers do not stack. Boosted Manga gifts keep the higher weekly allowance, never add both; current-week use is counted once at claim.')
        : htmlText('voucher.storeGiftLegacy', 'Your gift Moonlight follows its existing reset and end dates. Claiming a new gift replaces it with four fresh weeks.')}</p>`
    : '';
  let action;
  if (giftWaits) {
    action = isTheHeldPass
      ? `<button class="plan-btn primary" data-store-action="manage">${htmlText('store.manageSubscription', 'Manage Subscription')}</button>`
      : `<button class="plan-btn" data-store-action="manage">${htmlText('store.switchInPortal', 'Change plan')}</button>`;
  } else if (complimentary) {
    action = `<button class="plan-btn${highlight ? ' primary' : ''}" data-store-action="checkout" data-plan="${PLAN_TYPES[tier][period]}" data-replaces-complimentary="1">${htmlText('store.subscribe', 'Subscribe')}</button>`;
  } else if (prepayable) {
    action = `<button class="plan-btn primary" data-store-action="checkout" data-plan="${PLAN_TYPES[tier].year}">${htmlText('store.switchToYearly', 'Switch to yearly')}</button>`;
  } else if (isTheHeldPass) {
    action = `<button class="plan-btn primary" data-store-action="manage">${htmlText('store.manageSubscription', 'Manage Subscription')}</button>`;
  } else if (activeTier != null && !upgradeable) {
    /* A TIER BELOW THE ONE HELD IS NOT "your subscription". This said "Manage
       Subscription" — the same words as the held card — so a Manga Pass holder
       saw two identical Manage buttons and the Storyteller card read as though
       they owned it too. The action is still the billing portal, because a
       downgrade defers to period end, but the label now says what it does. */
    action = `<button class="plan-btn" data-store-action="manage">${htmlText('store.switchInPortal', 'Change plan')}</button>`;
  } else {
    const label = upgradeable ? htmlText('store.upgrade', 'Upgrade') : htmlText('store.subscribe', 'Subscribe');
    action = `<button class="plan-btn${highlight ? ' primary' : ''}" data-store-action="checkout" data-plan="${PLAN_TYPES[tier][period]}">${label}</button>`;
  }

  return `
    <div class="plan-card${highlight ? ' highlight' : ''}" data-pass-tier="${tier}">
      ${cardBadge ? `<div class="plan-badge${isTheHeldPass ? ' plan-badge--active' : ''}">${cardBadge}</div>` : ''}
      <div class="plan-art"><img class="plan-icon" src="assets/store/${icon}" alt="${htmlText(titleKey, titleFallback)}" decoding="async"></div>
      <div class="plan-title">${htmlText(titleKey, titleFallback)}</div>
      <div class="plan-price">${headline}<span>${htmlText('store.monthlyPeriod', '/mo')}</span></div>
      <div class="plan-tax-note">${taxNoteText(tax, htmlText)}</div>
      <div class="plan-saving${isYear ? '' : ' is-empty'}">${saving}</div>
      ${complimentaryLine}
      <ul class="plan-features">${features.map((f) => (typeof f === 'object' && f.note
        /* A NOTE CONTINUES THE LINE ABOVE. Given its own tick it read as a
           separate feature, which made "or around 100 with Pro Storyteller off"
           look like a second entitlement rather than the same Moonlight
           counted at the cheaper rate. */
        ? `<li class="plan-feature-note">${f.note}</li>`
        : `<li>${f}</li>`)).join('')}</ul>
      ${action}
    </div>`;
}

/**
 * Both tiers, at the selected period.
 *
 * Duel counts come from the generated mirror of the live cost model.
 *
 * NO PvP FIGURE: a duel's cost is split across its seats, so what one costs
 * YOU depends on who you play with. Quoting it read as a promise of a boost
 * that varies with the company you keep.
 * The owner's reason is the right one: at the moment of payment a range reads
 * as uncertainty about what you are buying, while a named figure
 * reads as a choice you get to make. Moonlight stays the unit being sold; the
 * counts are the reference that makes "more Moonlight" mean something.
 */
export function buildPassCards({
  htmlText, period, activeTier = null, activeInterval = null, complimentary = false, complimentaryEndsAt = '', giftPlan = null,
  tax = null, weeklyReset = false, giftFourWeeks = false,
}) {
  // The promo compares each pass's monthly play; reset timing belongs in
  // the Moonlight details rather than changing this reference every week.
  const storyteller = TIER_DUEL_ESTIMATES.moonfall_storyteller;
  const manga = TIER_DUEL_ESTIMATES.moonfall_manga;

  return buildCard({
    htmlText,
    period,
    activeTier,
    activeInterval,
    complimentary,
    complimentaryEndsAt,
    giftPlan,
    tax,
    giftFourWeeks,
    tier: 'moonfall_storyteller',
    /* NAMED FOR THE PRODUCT, NOT AN INTERVAL. These were `moonpass-monthly`
       and `moonpass-yearly` back when the store sold one pass in two billing
       periods. They now identify two TIERS — and with a Monthly/Yearly control
       on the same screen, art named "monthly" sitting on the Storyteller card
       is an invitation to swap them to match the selected period. */
    icon: 'pass-storyteller.webp',
    badge: '',
    highlight: false,
    titleKey: 'store.storytellerPass',
    titleFallback: 'Storyteller Pass',
    features: [
      /* ONE LINE THAT NAMES THE FEATURE AND EXPLAINS IT. These were two
         separate perks — "A smarter storyteller" and "A smarter AI opponent" —
         with "Pro Storyteller" mentioned only in the duel-count note below, so
         nothing told the reader those three phrases are the same switch. Since
         2026-09-24 every tier plays the same opponent (owner), so the perk is
         the storyteller alone and names no opponent. */
      htmlText('store.advancedAiPerk', 'Pro Storyteller (smarter storyteller)'),
      /* ONE LADDER, CHEAPEST FIRST — basic, Pro Storyteller, then manga — because
         the modes are a choice the player makes per duel, not three unrelated
         numbers. Pro Storyteller is ON by default, so its figure is the one they
         will actually see first; naming basic first shows what the same
         Moonlight is worth if they turn it off. An "or" introduces every rung
         after the first (owner, 2026-09-12): without it the counts read as a
         running total, when each is the same Moonlight spent a different way. */
      htmlText('store.duelLadder', 'Moonlight for around {{normalAi}} duels per month, or {{solo}} with Pro Storyteller', storyteller),
    ],
  }) + buildCard({
    htmlText,
    period,
    activeTier,
    activeInterval,
    complimentary,
    complimentaryEndsAt,
    giftPlan,
    tax,
    giftFourWeeks,
    tier: 'moonfall_manga',
    icon: 'pass-manga.webp',
    badge: htmlText('store.bestValue', 'BEST VALUE'),
    highlight: true,
    titleKey: 'store.mangaPass',
    titleFallback: 'Manga Pass',
    features: [
      /* The perk by name, not "Everything in Storyteller Pass" — the owner's
         call (2026-09-19): the premium card should show the concrete thing
         the price buys instead of pointing at the other card; the ladder
         line below already says it comes with Pro Storyteller. */
      htmlText('store.proStorytellerIncluded', 'Pro Storyteller'),
      htmlText('store.mangaGenerationEnabled', 'Manga storytelling'),
      htmlText('store.mangaDuelLadder', 'Moonlight for around {{normalAi}} duels per month, or {{solo}} with Pro Storyteller, or {{manga}} with Manga', manga),
    ],
  });
}
